<template>
  <div
    :class="['series-container', boxset ? 'boxset' : '', dynamicWidth ? 'self-sizing' : '']"
    v-press="navigateToSeries"
  >
    <div :class="['image-wrapper', boxset ? 'boxset-ratio' : 'video-ratio']">
      <TG4Image
        :image="image"
        :aspect="boxset ? 'portrait' : 'landscape'"
        :badge="series.badge"
        :alt="series.name"
      />
      <div v-if="overlay && !boxset" class="overlay-title" :style="textStyle">
        <h2>{{ series.name }}</h2>
      </div>
    </div>
    <div v-if="!overlay || boxset" class="title-container" :style="textStyle">
      <h2>{{ series.name }}</h2>
      <h3 v-if="showCategory">{{ categoryKey ? $t(categoryKey) : '' }}</h3>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance } from 'vue';
import { RouteName, routeTo } from '@/router/routes';
import { isSeriesCategory, SeriesResponse } from '@/types/cms';
import TG4Image from '@/components/TG4Image.vue';

const props = withDefaults(defineProps<{
  series: SeriesResponse;
  boxset?: boolean;
  overlay?: boolean;
  dynamicWidth?: boolean;
  showCategory?: boolean;
  textColor?: string;
}>(), {
  showCategory: false,
});

const instance = getCurrentInstance();
const router = instance?.proxy?.$router;

const navigateToSeries = () => {
  router?.push(routeTo(RouteName.Series, { slugOrVID: props.series.slug }));
};

const image = computed(() => (props.boxset ? props.series.boxsetImage : props.series.mainImage));

const categoryKey = computed(() => {
  const raw = props.series.rawCategory;
  return isSeriesCategory(raw) ? `category.${raw}` : null;
});

const textStyle = computed(() => {
  if (props.textColor) {
    return {
      color: props.textColor,
    };
  }
  return {};
});

</script>

<style scoped lang="scss">
.series-container {
  &:focus {

    outline: 0;

    .image-wrapper {
      border-color: $color-pink;
    }

    .title-container,
    .overlay-title {
      @include text-white;
    }
  }
}


.image-wrapper {
  position: relative;
  margin-top: 5px;
  border: $focus-border-width solid transparent;
  box-sizing: border-box;
}

.video-ratio {
  width: $series-cell-width;
}

.boxset-ratio {
  width: $boxset-cell-width;
}

// Two classes deep so these beat both the rules above and the small-screen
// overrides at the bottom of the file, whatever the source order.
.series-container.self-sizing {
  .video-ratio,
  .boxset-ratio {
    width: 100%;
  }

  .title-container,
  .title-container h2 {
    max-width: 100%;
  }
}

.title-container {
  width: 100%;
  text-align: left;
  max-width: 100%;
  margin-bottom: 10px;

  @include text-white;

  h2 {
    margin: 0;
    @include text-cell-title;
    @include truncate;
  }

  h3 {
    margin: 0;
    // Stays in the layout even when the series has no category, so every cell
    // in the rail keeps the same height - tvOS empties this label rather than
    // hiding it.
    min-height: $text-cell-subtitle * $line-height-body;
    @include text-cell-subtitle;
    @include truncate;
  }
}

.overlay-title {
  position: absolute;
  left: 25px;
  right: 25px;
  bottom: 5px;
  padding: 40px 15px 10px 15px;
  text-align: left;
  @include text-white;
  background: linear-gradient(to top, black, rgba(0, 0, 0, 0));

  h2 {
    margin: 0;
    @include text-cell-title;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
}

.boxset {
  .title-container {
    max-width: $boxset-cell-width;

    h2 {
      max-width: $boxset-cell-width;
    }
  }
}
</style>
